/**
 * Recherche prédictive — module « combobox ARIA », enregistré sous
 * `window.MintPredictiveSearch.combobox`, chargé en `<script defer>` AVANT
 * `predictive-search.js`. Responsabilité unique : ARIA et clavier — attributs de l'input et du
 * panneau, option active aux flèches, Entrée / Escape / Tab / clic, défilement, région live,
 * génération des `id` d'options.
 *
 * `id` préfixés par instance : le même panneau est injecté jusqu'à 3 fois dans le même
 * document (header desktop, header mobile, drawer) avec la MÊME réponse de section — des `id`
 * fixes rendus en Liquid seraient des doublons. Préfixe dérivé de `data-predictive-location`,
 * unique par parcours, jamais d'un compteur global qui repartirait de zéro à chaque
 * `shopify:section:load`.
 *
 * `role="listbox"` ne va PAS sur le wrapper, qui porte aussi les récentes, la région live et le
 * bouton « voir tous les résultats », quand un listbox n'accepte que `option` et `group`
 * (Q-S4) : il va sur les deux conteneurs d'état, qui ne contiennent que des `group` —
 * `[data-predictive-empty]` (A) et `[data-predictive-listbox]` (B, injecté). Ils s'excluent
 * (T22), donc un seul est exposé à la fois, et `aria-controls` désigne le wrapper (le popup) :
 * `aria-activedescendant` reste résoluble dans les deux états. DÉVIATION ASSUMÉE, la seule :
 * les lignes de `[data-predictive-recent]` sont navigables (T24 l'exige) et reçoivent
 * `role="option"` sans que leur conteneur soit un listbox — chacune porte une croix.
 *
 * Frontière avec T22 / T27 : ce module ouvre et ferme le panneau (`panel.hidden`), parce que
 * `aria-expanded` ne peut pas diverger de la visibilité réelle du popup ; il n'arbitre pas ce
 * qu'il y a dedans. La bascule A ↔ B reste à T22, l'exclusion mutuelle à T27, qui appellent
 * `close()` et écoutent `predictive-search:open` / `:close`.
 * `autocomplete="off"`, `role="combobox"` et `aria-expanded="false"` dès le rendu serveur, et
 * `aria-label` sur les deux listbox, sont posés par T25 dans le markup (header.liquid,
 * header-fmd.liquid, search-bar.liquid, predictive-search-panel.liquid) : ce module les
 * retrouve déjà là et se contente de maintenir `aria-expanded`/`role="combobox"` synchronisés à
 * l'exécution. Asset séparé, jamais un bloc `javascript` de section : raison en tête de
 * `predictive-search.js`.
 */
(function (window, document) {
  'use strict';

  var registry = (window.MintPredictiveSearch = window.MintPredictiveSearch || {});

  /** Air laissé sous le panneau, pour qu'il ne colle pas au bord de l'écran. */
  var MARGE_BAS = 16;
  /** Plancher : sous cette hauteur le panneau ne vaut plus rien, autant le laisser déborder. */
  var HAUTEUR_MIN = 180;
  var ID_NAMESPACE = 'PredictiveOption';

  // Une option = tout ce qui porte `data-suggestion-type`, posé par les quatre producteurs :
  // état A (T14), catégories et articles (T13), carte produit (T9), lignes récentes (T26).
  // Seul sélecteur uniforme aux deux états — `[data-cta-name]` attraperait aussi des boutons.
  var OPTION_SELECTOR = '[data-suggestion-type]';

  // Les deux conteneurs d'état, seuls à ne contenir que des `group` (cf. en-tête).
  var LISTBOX_SELECTOR = '[data-predictive-empty], [data-predictive-listbox]';

  // Préfixes des instances vivantes : deux d'entre elles ne peuvent pas produire le même `id`.
  var takenPrefixes = new Set();

  // `header_fmd_desktop` devient `header-fmd-desktop`.
  function slugify(location) {
    var slug = String(location).toLowerCase();
    return slug.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  // La location suffit tant que T3 tient son unicité ; le suffixe n'est qu'un filet.
  function takePrefix(location) {
    var base = ID_NAMESPACE + '-' + slugify(location);
    var candidate = base;
    var suffix = 2;
    while (takenPrefixes.has(candidate)) {
      candidate = base + '-' + suffix;
      suffix += 1;
    }
    takenPrefixes.add(candidate);
    return candidate;
  }

  // Visible = aucun `hidden` sur le noeud ni sur un ancêtre SOUS le panneau ; le `hidden` du
  // panneau lui-même (fermé) ne compte pas, sinon aucune option ne serait jamais indexée.
  function isVisibleIn(node, root) {
    var blocker = node.closest('[hidden]');
    return !blocker || blocker === root || !root.contains(blocker);
  }

  /** @returns {object|null} `null` si `{input, panel, location}` est incomplet — l'hôte se dégrade sans erreur. */
  function createCombobox(options) {
    var config = options || {};
    if (!config.input || !config.panel || !config.location) return null;

    var input = config.input;
    var panel = config.panel;
    var prefix = takePrefix(config.location);
    var state = {
      activeIndex: -1,
      options: [],
      expanded: false,
      // Escape traité en `keydown` ; ce drapeau fait taire le `keyup` qui suit (cf. `onKeyup`).
      swallowEscapeKeyup: false,
      observer: null,
      // Cf. `accordionContainer` : `null` tant que cette instance n'en a pas un.
      accordionObserver: null,
      listeners: [],
    };

    /**
     * Tâche hors plan (2026-09-16) : quand `#MobileSearchContainer` se referme par le
     * CHEMIN DU THÈME — `closeSearchDrawer()` dans `assets/theme.js`, déclenché par un clic
     * extérieur, Escape, un swipe ou l'ouverture du drawer/du tiroir de filtres — ce module
     * n'en est jamais informé : `closeSearchDrawer()` retire `.is-active` du conteneur et
     * anime son `max-height` à 0, mais ne touche ni `[data-predictive-wrapper]` (donc le
     * `MutationObserver` de `panel` plus bas ne voit rien) ni ne déclenche d'event
     * `predictive-search:*`. Résultat mesuré : plus rien n'est visible, mais
     * `aria-expanded="true"` reste posé sur l'input et `panel.hidden` reste `false` — menteur
     * pour un lecteur d'écran, qui annonce un popup ouvert qui ne l'est plus.
     * Seule l'instance mobile standard (`header_mobile`) est concernée : c'est la SEULE à
     * vivre dans un `.site-header__search-container` (l'accordéon du module classique de
     * `theme.js`) — le desktop, le drawer et les deux emplacements FMD ne l'ont pas
     * (`.header-fmd` mobile utilise `.secondary__menu-mobile`, toujours en flux, jamais
     * replié). `closest()` rend donc `null` ailleurs et l'observateur ne se pose pas :
     * aucun effet de bord sur les quatre autres emplacements.
     * Observé plutôt qu'écouté : `closeSearchDrawer()` vit dans `assets/theme.js`, qu'il est
     * interdit de toucher ici (reformaté en entier par un hook au moindre edit, cf.
     * anti-pattern n°40) — c'est le SEUL signal stable qu'il expose sans qu'on le modifie.
     * On ne réagit qu'au RETRAIT de `is-active` : l'AJOUT (ouverture par le déclencheur
     * loupe) ne doit jamais ouvrir le panneau de lui-même, seul un focus/clic sur le champ
     * le fait (Q-S11, `predictive-search-states.js`).
     */
    var accordionContainer = panel.closest('.site-header__search-container');

    function optionId(index) {
      return prefix + '-' + index;
    }

    // Garde de quoi retirer chaque écouteur : `destroy()` doit tout défaire, `document` compris.
    function listen(target, type, handler, listenerOptions) {
      target.addEventListener(type, handler, listenerOptions);
      state.listeners.push({ target: target, type: type, handler: handler, options: listenerOptions });
    }

    function emit(name) {
      var detail = { location: config.location };
      input.dispatchEvent(new CustomEvent('predictive-search:' + name, { bubbles: true, detail: detail }));
    }

    // Réutilisée si le markup en fournit une (T30/T31/T32), créée sinon. Elle doit vivre dans la
    // page hôte et NON dans la réponse de section : recréée à chaque injection, elle n'annoncerait
    // pas de façon fiable.
    function resolveLiveRegion() {
      var existing = panel.querySelector('[data-predictive-live]');
      if (existing) return existing;
      var node = document.createElement('p');
      node.className = 'predictive-panel__announce visually-hidden';
      node.setAttribute('data-predictive-live', '');
      node.setAttribute('aria-live', 'polite');
      node.setAttribute('aria-atomic', 'true');
      panel.insertBefore(node, panel.firstChild);
      return node;
    }

    var live = resolveLiveRegion();

    // Recopie le compte rendu par le panneau (T13/T34) ; le garde évite de ré-annoncer l'identique.
    function announce() {
      var source = panel.querySelector('[data-predictive-announce]');
      var text = source ? source.textContent.trim() : '';
      if (text !== live.textContent) live.textContent = text;
    }

    // Ré-indexe les options visibles des DEUX états, à chaque mutation : l'état B est remplacé en
    // entier à chaque réponse et les récentes se re-rendent. L'option active est retrouvée par
    // identité de noeud, jamais par rang, qui repasse à -1 avant de re-marquer.
    function syncOptions() {
      var previous = state.options[state.activeIndex] || null;
      var list = [];
      // Le rang court sur TOUTES les options du panneau, masquées comprises : n'indexer que les
      // visibles rendait l'`id` périmé d'une option de l'état masqué identique à celui d'une
      // option vivante (mesuré : 12 `id` uniques pour 18). `state.options` ne retient que les visibles.
      panel.querySelectorAll(OPTION_SELECTOR).forEach(function (node, index) {
        node.id = optionId(index);
        node.setAttribute('role', 'option');
        node.setAttribute('aria-selected', 'false');
        if (isVisibleIn(node, panel)) list.push(node);
      });
      state.options = list;
      state.activeIndex = -1;
      panel.querySelectorAll(LISTBOX_SELECTOR).forEach(function (node) {
        node.setAttribute('role', 'listbox');
      });
      setActive(previous ? list.indexOf(previous) : -1, false);
    }

    // Une seule option porte `aria-selected="true"`, celle que pointe `aria-activedescendant`.
    function setActive(index, scroll) {
      var previous = state.options[state.activeIndex];
      if (previous) previous.setAttribute('aria-selected', 'false');
      state.activeIndex = index;
      var current = state.options[index];
      if (!current) {
        input.removeAttribute('aria-activedescendant');
        return;
      }
      current.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', current.id);
      // Panneau `overflow: auto` : sans ceci l'option active sort du champ visible dès la
      // 5e ou 6e flèche (Q-S6).
      if (scroll && typeof current.scrollIntoView === 'function') current.scrollIntoView({ block: 'nearest' });
    }

    // Bornée aux deux extrémités, comme les deux comboboxes du thème. `-1` = retour à l'input.
    function move(step) {
      syncOptions();
      if (!state.options.length) return;
      setActive(Math.max(-1, Math.min(state.activeIndex + step, state.options.length - 1)), true);
    }

    // Aligne `aria-expanded` sur la visibilité RÉELLE du popup, quel que soit qui l'a changée.
    function sync() {
      state.expanded = !panel.hidden;
      input.setAttribute('aria-expanded', state.expanded ? 'true' : 'false');
      if (!state.expanded) input.removeAttribute('aria-activedescendant');
    }

    /**
     * Borne la hauteur du panneau sur la place RÉELLEMENT disponible sous lui.
     * La feuille ne peut pas la calculer seule : son `max-height` est exprimé en
     * `100vh - 100%`, où `100%` est la hauteur du bloc conteneur — soit la barre
     * de recherche (44px) depuis que le panneau y est ancré, et non plus le header
     * entier. Le panneau pouvait donc descendre d'une trentaine de pixels SOUS le
     * bas de l'écran, rendant le pied « voir tous les résultats » inatteignable
     * même en faisant défiler (signalé le 2026-09-16).
     * Mesuré à l'ouverture et non au chargement : la position dépend du header
     * sticky, du bandeau d'annonce et de la largeur.
     */
    function bornerHauteur() {
      if (typeof panel.getBoundingClientRect !== 'function') return;
      /*
       * Instance EN FLUX (tiroir mobile, cf. `#NavDrawer [data-predictive-wrapper]` dans
       * predictive-search.css) : on ne borne RIEN. Sa `max-height` y est exprimée en unité
       * de viewport (`60vh`), donc parfaitement résolvable — le calcul ci-dessous, lui, part
       * du `top` du panneau et donne une valeur PLUS GRANDE que le tiroir, qu'il écrase
       * ensuite en style inline. Mesuré le 2026-09-16 à 390x844, tiroir ouvert puis champ
       * focalisé : 621px inline contre 506px en feuille, panneau à 585px, et
       * `.drawer__scrollable` (le menu) repoussé à `top 857` pour un écran de 844 avec 20px
       * de haut — le menu sort de l'écran et le tiroir devient inutilisable.
       * Le style inline est retiré et non simplement ignoré : sans cela, une valeur posée
       * avant ce garde survivrait à la cascade.
       */
      if (typeof window.getComputedStyle === 'function') {
        var place = window.getComputedStyle(panel).position;
        if (place !== 'absolute' && place !== 'fixed') {
          if (panel.style.maxHeight) panel.style.maxHeight = '';
          return;
        }
      }
      var haut = panel.getBoundingClientRect().top;
      if (!isFinite(haut)) return;
      /*
       * `window.innerHeight` et NON `visualViewport.height` : le panneau s'étend derrière le
       * clavier logiciel, il ne s'arrête pas au-dessus (décision utilisateur 2026-09-16,
       * après avoir vu les deux). Borner sur la fenêtre visuelle donnait un panneau coupé
       * net au-dessus du clavier ; la hauteur de fenêtre le laisse occuper tout l'écran, et
       * son défilement interne amène le pied au-dessus du clavier quand on le parcourt.
       */
      var dispo = Math.max(window.innerHeight - haut - MARGE_BAS, HAUTEUR_MIN);
      panel.style.maxHeight = Math.round(dispo) + 'px';
    }

    /**
     * La position du panneau n'est pas acquise à l'instant du focus, et une seule mesure
     * ne suffit donc pas.
     *
     * Mesuré le 2026-09-16 (recette T46, 390 px, reproduit deux fois) : le conteneur
     * `.site-header__search-container` se révèle par un slide de 280 ms
     * (`transform: translateY(-100%) -> 0`, `mint.css`). Un focus arrivant AVANT la fin de
     * cette transition — double-tap rapide, activation clavier immédiate, harnais de test —
     * lit un `top` encore translaté (61 au lieu de 124 mesuré), fige une `max-height` trop
     * grande (767 au lieu de 704), et le panneau déborde ensuite le bas de l'écran de 47 px
     * SANS possibilité de recalcul tant qu'on ne referme pas.
     *
     * On remesure donc à chaque fin de transition du conteneur, plus une passe à la frame
     * suivante : `transitionend` ne se déclenche pas si aucune transition n'est en cours
     * (panneau déjà ouvert, ou emplacement sans accordéon comme le header FMD), et la frame
     * suivante suffit à rattraper un simple reflow. Les deux sont idempotentes.
     */
    function planifierRemesure() {
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(function () {
          if (state.expanded) bornerHauteur();
        });
      }
      var accordeon = typeof panel.closest === 'function' ? panel.closest('.site-header__search-container') : null;
      if (!accordeon) return;
      var surFin = function () {
        accordeon.removeEventListener('transitionend', surFin);
        if (state.expanded) bornerHauteur();
      };
      accordeon.addEventListener('transitionend', surFin);
    }

    function open() {
      if (state.expanded) return;
      panel.hidden = false;
      bornerHauteur();
      planifierRemesure();
      sync();
      syncOptions();
      emit('open');
    }

    function close(restoreFocus) {
      if (!state.expanded) return;
      setActive(-1, false);
      panel.hidden = true;
      sync();
      // Sans le garde, ce `focus()` rouvrirait le panneau via l'écouteur `focus` (mesuré).
      if (restoreFocus && document.activeElement !== input) input.focus();
      emit('close');
    }

    function onKeydown(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!state.expanded) {
          open();
          return;
        }
        move(1);
      } else if (event.key === 'ArrowUp') {
        if (!state.expanded) return;
        event.preventDefault();
        move(-1);
      } else if (event.key === 'Enter') {
        // Sans option active on ne touche à rien : le formulaire se soumet (T21).
        var active = state.options[state.activeIndex];
        if (!state.expanded || !active) return;
        event.preventDefault();
        active.click();
      } else if (event.key === 'Escape') {
        if (!state.expanded) return;
        event.preventDefault();
        state.swallowEscapeKeyup = true;
        close(true);
      } else if (event.key === 'Tab') {
        close(false);
      }
    }

    // Les deux modules header ferment tout le conteneur de recherche sur `keyup` Escape
    // (`document` pour l'un, `window` pour l'autre) : un `keydown` seul n'empêcherait rien. On ne
    // bloque QUE le `keyup` consommé — panneau déjà fermé, Escape doit fermer le conteneur.
    function onKeyup(event) {
      if (event.key !== 'Escape' || !state.swallowEscapeKeyup) return;
      state.swallowEscapeKeyup = false;
      event.stopPropagation();
    }

    function onOutsidePointer(event) {
      if (!state.expanded) return;
      if (input.contains(event.target) || panel.contains(event.target)) return;
      close(false);
    }

    function destroy() {
      state.listeners.forEach(function (entry) {
        entry.target.removeEventListener(entry.type, entry.handler, entry.options);
      });
      state.listeners = [];
      if (state.observer) {
        state.observer.disconnect();
        state.observer = null;
      }
      if (state.accordionObserver) {
        state.accordionObserver.disconnect();
        state.accordionObserver = null;
      }
      input.removeAttribute('aria-activedescendant');
      state.options = [];
      state.activeIndex = -1;
      takenPrefixes.delete(prefix);
    }

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    if (!panel.id) panel.id = prefix + '-popup';
    input.setAttribute('aria-controls', panel.id);
    sync();
    syncOptions();
    announce();

    // Aucun `tabindex` sur les options : `trapFocus` fige la liste des focusables à l'ouverture
    // du drawer, et `aria-activedescendant` permet de naviguer sans jamais quitter l'input —
    // c'est aussi pourquoi le clic double le focus : après Escape le champ GARDE le focus, donc
    // `focus` ne se redéclenche pas et un re-clic ne rouvrirait rien (mesuré).
    listen(input, 'focus', open);
    listen(input, 'click', open);
    listen(input, 'keydown', onKeydown);
    listen(input, 'keyup', onKeyup);
    listen(document, 'pointerdown', onOutsidePointer);
    listen(window, 'resize', function () {
      if (state.expanded) bornerHauteur();
    });
    state.observer = new MutationObserver(function () {
      sync();
      syncOptions();
      announce();
    });
    state.observer.observe(panel, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });

    // Cf. le commentaire de `accordionContainer` plus haut : synchronise ce module sur une
    // fermeture décidée par `assets/theme.js`, jamais l'inverse (l'ouverture du conteneur ne
    // doit rien déclencher ici).
    if (accordionContainer) {
      state.accordionObserver = new MutationObserver(function () {
        if (state.expanded && !accordionContainer.classList.contains('is-active')) close(false);
      });
      state.accordionObserver.observe(accordionContainer, { attributes: true, attributeFilter: ['class'] });
    }

    return {
      idPrefix: prefix,
      state: state,
      optionId: optionId,
      listen: listen,
      open: open,
      close: close,
      refreshOptions: syncOptions,
      destroy: destroy,
    };
  }

  registry.combobox = { create: createCombobox };
})(window, document);
